import { Button, Skeleton, Stack, Text } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import type { MessageKey } from "../../shared/i18n";
import { endpoints, type OrganizationRole } from "../api/client";
import { invitationQuery, meQuery, organizationsQuery, useNotifyError } from "../api/queries";
import { useAuth } from "../auth/provider";
import { useMessages } from "../i18n/provider";
import { AuthLayout } from "./auth/layout";

const ROLE_KEYS: Record<OrganizationRole, MessageKey> = {
  owner: "organization.role.owner",
  admin: "organization.role.admin",
  member: "organization.role.member",
};

export function InvitePage() {
  const { token = "" } = useParams();
  const t = useMessages();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifyError = useNotifyError();
  const { isAuthenticated, isLoading: authLoading, me, signOut } = useAuth();

  const { data, isLoading, isError } = useQuery({
    ...invitationQuery(token),
    enabled: Boolean(token),
  });

  const accept = useMutation({
    mutationFn: () => endpoints.acceptInvitation(token),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: meQuery.queryKey }),
        queryClient.invalidateQueries({ queryKey: organizationsQuery.queryKey }),
        queryClient.invalidateQueries({ queryKey: ["organization", result.organizationId] }),
      ]);
      navigate(`/organization/${result.organizationId}`);
    },
    onError: notifyError,
  });

  if (isLoading || authLoading) {
    return (
      <AuthLayout title={t("organization.title")}>
        <Skeleton height={18} radius="sm" />
        <Skeleton height={18} width="70%" radius="sm" mt={10} />
        <Skeleton height={36} radius="md" mt={24} />
      </AuthLayout>
    );
  }

  if (isError || !data) {
    return (
      <AuthLayout title={t("organization.title")}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t("organization.inviteNotFound")}
          </Text>
          <Button variant="light" onClick={() => navigate("/")}>
            {t("common.back")}
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  const title = t("organization.acceptTitle", { name: data.organizationName });
  const role = t(ROLE_KEYS[data.role]);
  const body = t("organization.acceptBody", { email: data.email, role });
  const wrongAccount = isAuthenticated && me?.email.toLowerCase() !== data.email.toLowerCase();

  if (data.accepted || data.expired) {
    return (
      <AuthLayout title={title}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {data.accepted ? t("errors.inviteUsed") : t("errors.inviteExpired")}
          </Text>
          <Button onClick={() => navigate(isAuthenticated ? "/organization" : "/")}>
            {isAuthenticated ? t("organization.backToOrgs") : t("common.back")}
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={title}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {body}
        </Text>

        {!isAuthenticated ? (
          <>
            <Text size="sm" c="dimmed">
              {t("organization.signInToAccept")}
            </Text>
            <Button component="a" href={`/login?next=${encodeURIComponent(`/invite/${token}`)}`}>
              {t("nav.signIn")}
            </Button>
          </>
        ) : wrongAccount ? (
          <>
            <Text size="sm" c="dimmed">
              {t("organization.wrongAccount", {
                current: me?.email ?? "",
                email: data.email,
              })}
            </Text>
            <Button
              variant="light"
              onClick={async () => {
                await signOut();
                navigate(`/login?next=${encodeURIComponent(`/invite/${token}`)}`);
              }}
            >
              {t("organization.switchAccount")}
            </Button>
          </>
        ) : (
          <Button loading={accept.isPending} onClick={() => accept.mutate()}>
            {t("organization.accept")}
          </Button>
        )}

        {isAuthenticated ? (
          <Button variant="subtle" color="gray" onClick={() => navigate("/organization")}>
            {t("organization.backToOrgs")}
          </Button>
        ) : null}
      </Stack>
    </AuthLayout>
  );
}
